/**
 * Heads-Up NLHE Poker 盤コンポーネント。
 *
 * レイアウト:
 *   - 上端: 相手 (= 対面プレイヤー) のホール 2 枚 (mini では裏向き、 staticView/Showdown では表)
 *           + 残スタック + 現在 bet + SB/BB バッジ
 *   - 中央: コミュニティカード (board) 0..=5 枚 + pot 表示
 *   - 下端: 自分のホール 2 枚 (= 自分が誰の Seat かを判定して表示)
 *           + 残スタック + 現在 bet + SB/BB バッジ
 *   - 最下端: アクションボタン群 (Fold / Check / Call / Bet / Raise / AllIn)
 *           + ベットスライダ (Bet/Raise 額の調整)
 *
 * MVP では mental poker (commit-reveal) は engine 側で stub 状態。 hole はサーバが直接知る形なので、
 * 自分のホールはそのまま state.current_hand.hole[my_seat] を表示。 完全実装時は private state へ移行。
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  Card,
  PokerMatchState,
  PokerMove,
  Seat,
  Side,
} from '../../games/heads-up-poker';
import { isBet, isRaise } from '../../games/heads-up-poker';
import { PokerCard, PokerInlineCard } from './PokerCard';
export { PokerCard } from './PokerCard';
import { useRoom } from '../../state/room';

/** revealedBoard 未設定時の安定 ref (= store fallback で毎 render 新規配列を作らない)。 */
const EMPTY_BOARD: Card[] = [];

/**
 * 直近ハンドの結果を「左サイドペイン」 にスタックする履歴エントリ。
 * - winner / pot / 両者ホールカード (Showdown 開示時のみ) を保持。
 * - hand_complete 検知時 (= h.winner が set された瞬間) に 1 件だけ push。
 * - state は次ハンドで current_hand が完全に差し替わるため、 board 側でしか
 *   「前ハンドの結果」 を覚えておくレイヤがない。
 */
interface HandRecord {
  hand_number: number;
  result: 'win' | 'lose' | 'chop';
  pot: number;
  /** 終局時のコミュニティボード (0..=5 枚)。 fold で flop に届かなければ空。 */
  board: Card[];
  sb_hole: [Card, Card] | null;
  bb_hole: [Card, Card] | null;
  my_was_sb: boolean | null;
}

const NEXT_HAND_AUTO_DELAY_MS = 900;
const HISTORY_MAX = 4;

/** プリフロップのオープンサイズ (= BB 倍率で to_total を決める)。 */
const PREFLOP_OPENS: { label: string; mult: number }[] = [
  { label: '2bb', mult: 2 },
  { label: '2.5bb', mult: 2.5 },
  { label: '3bb', mult: 3 },
];
/** ポストフロップのベット/レイズサイズ (= ポットに対する割合)。 */
const POT_FRACTIONS: { label: string; frac: number }[] = [
  { label: '33%', frac: 1 / 3 },
  { label: '50%', frac: 0.5 },
  { label: '100%', frac: 1 },
];

interface Props {
  state: PokerMatchState;
  legal?: PokerMove[];
  yourSide?: Side | null;
  myTurnAllowed?: boolean;
  onMove?: (mv: PokerMove) => void;
  mini?: boolean;
  lastMove?: PokerMove;
  staticView?: boolean;
  /** Board id (= MatchState.boards[i].board)。 mental poker protocol で per-board×hand
   *  の relay key として必須。 mini / staticView 経路では undefined OK。 */
  board?: number;
}

/** Side -> ハンド内 Seat (= SB or BB)。 dealer_side と一致した側が SB。 */
function sideToSeat(side: Side | null | undefined, dealerSide: Side): Seat | null {
  if (side == null) return null;
  return side === dealerSide ? 'sb' : 'bb';
}


function ChipStack({ amount }: { amount: number }) {
  return (
    <span className="poker-chip-stack">
      <span className="poker-chip-icon" aria-hidden />
      <span className="poker-chip-amount">{amount}</span>
    </span>
  );
}

export function HeadsUpPokerBoard({
  state,
  legal,
  yourSide,
  myTurnAllowed,
  onMove,
  mini,
  staticView,
  board,
}: Props) {
  const h = state.current_hand;
  const mySeat = sideToSeat(yourSide ?? null, state.current_dealer_side);
  const oppSeat: Seat | null = mySeat == null ? null : mySeat === 'sb' ? 'bb' : 'sb';
  const showDown =
    h.phase === 'showdown' ||
    h.phase === 'hand_complete' ||
    h.phase === 'match_complete' ||
    h.phase === 'all_in_run_out';

  const isMyTurn =
    myTurnAllowed !== undefined
      ? myTurnAllowed
      : mySeat != null && h.to_act === mySeat;
  const showOppHint = !staticView && !isMyTurn && h.phase !== 'hand_complete' &&
    h.phase !== 'match_complete';

  const myStack = mySeat == null ? 0 : h.stacks[seatIdx(mySeat)];
  const oppStack = oppSeat == null ? 0 : h.stacks[seatIdx(oppSeat)];
  const myRoundBet = mySeat == null ? 0 : h.current_round_bet[seatIdx(mySeat)];
  const oppRoundBet = oppSeat == null ? 0 : h.current_round_bet[seatIdx(oppSeat)];
  // 自分の hole は engine broadcast (= per-seat mask 済) ではなく、 client が mental poker
  // で復号した crypto.myHole を真実とする。 broadcast の hole[mySeat] はサーバ mask に依存し、
  // オンライン対戦では mask の有無/正誤に晒される。 client は自分の hole を確実に知っているので
  // そちらを優先し、 静的再生 (= crypto 非 active) 時のみ engine 値に fallback する。
  const engineMyHole = mySeat == null ? null : h.hole[seatIdx(mySeat)];
  const oppHole = oppSeat == null ? null : h.hole[seatIdx(oppSeat)];

  const toCall = oppRoundBet - myRoundBet;
  // 合法手から各種ボタンの可否を判定
  const can = useMemo(() => {
    const set = new Set<string>();
    for (const m of legal ?? []) {
      if (m === 'fold') set.add('fold');
      else if (m === 'check') set.add('check');
      else if (m === 'call') set.add('call');
      else if (m === 'all_in') set.add('all_in');
      else if (m === 'next_hand') set.add('next_hand');
      else if (isBet(m)) set.add('bet');
      else if (isRaise(m)) set.add('raise');
    }
    return set;
  }, [legal]);
  const canNextHand = can.has('next_hand');
  const matchOver = state.outcome != null;

  // Bet/Raise スライダの状態
  const minBet = Math.max(1, h.blinds[1]);
  const minRaiseTo = oppRoundBet + h.min_raise_step;
  const maxBet = myStack;
  const maxRaiseTo = myRoundBet + myStack;
  const isRaiseMode = toCall > 0;
  const initialSlider = isRaiseMode ? minRaiseTo : minBet;
  const [betValue, setBetValue] = useState<number>(initialSlider);

  // mode 変化で slider を再初期化
  useMemo(() => {
    setBetValue(isRaiseMode ? minRaiseTo : minBet);
  }, [isRaiseMode, minRaiseTo, minBet]);

  // ===== クイックベットサイズ =====
  // プリフロップ (board 0 枚) = 2bb / 2.5bb / 3bb open、 ポストフロップ = 33/50/100% pot。
  // ボタンは slider 値 (betValue) を更新するだけ。 確定は Bet/Raise ボタンで行う (= 微調整も slider で可能)。
  const isPreflop = h.board.length === 0;
  const bb = Math.max(1, h.blinds[1]);
  const potNow = h.pot + h.current_round_bet[0] + h.current_round_bet[1];
  const clampTo = (v: number) =>
    isRaiseMode
      ? Math.max(minRaiseTo, Math.min(maxRaiseTo, Math.round(v)))
      : Math.max(minBet, Math.min(maxBet, Math.round(v)));
  /** プリフロップ open: to_total = bb × mult (raise/bet どちらでも) */
  const sizeByBb = (mult: number) => clampTo(bb * mult);
  /** ポット割合: bet 時は pot×frac、 raise 時は コール後ポット×frac を上乗せした to_total */
  const sizeByPot = (frac: number) =>
    isRaiseMode ? clampTo(oppRoundBet + (potNow + toCall) * frac) : clampTo(potNow * frac);
  /** All-in 額 (= slider の最大値)。 サイズボタンはこの値を slider にセットする。 */
  const allInTo = isRaiseMode ? maxRaiseTo : maxBet;
  const canBetOrRaise = can.has('bet') || can.has('raise');
  /** 確定: slider が最大 (= all-in) なら all_in を送る (= short stack の不正 raise 回避)。 */
  const commitBet = () => {
    if (isRaiseMode) {
      if (betValue >= maxRaiseTo) onMove?.('all_in');
      else onMove?.({ raise: { to_total: betValue } });
    } else {
      if (betValue >= maxBet) onMove?.('all_in');
      else onMove?.({ bet: { amount: betValue } });
    }
  };

  // 直近 N ハンドの結果スタック。 engine の `state.last_completed_hand` を source of truth
  // にして「ハンド時点の dealer_side」 を summary から取り出す。 これにより state.current_dealer_side
  // が start_next_hand で rotate された後に履歴 capture が走っても 勝/負 が逆転しない。
  // (= 旧実装は `state.current_dealer_side` から mySeat 計算していたため race で逆転)
  const [history, setHistory] = useState<HandRecord[]>([]);
  const lastCapturedHandRef = useRef<number>(-1);
  const summary = state.last_completed_hand;

  useEffect(() => {
    if (mini || staticView) return;
    if (summary == null) return;
    if (lastCapturedHandRef.current === summary.hand_number) return;
    lastCapturedHandRef.current = summary.hand_number;
    // ハンド時点の dealer_side で「自分が SB だったか」 を逆算 (= state.current_dealer_side
    // は既に新ハンドの値かもしれない)。 yourSide が null (= 観戦者) なら my_was_sb=null。
    const myWasSb = yourSide == null ? null : yourSide === summary.dealer_side_at_time;
    const w = summary.winner;
    const mySeatAtHand: Seat | null = myWasSb == null ? null : myWasSb ? 'sb' : 'bb';
    const result: HandRecord['result'] =
      w.length === 2
        ? 'chop'
        : mySeatAtHand != null && w[0] === mySeatAtHand
          ? 'win'
          : 'lose';
    setHistory((prev) =>
      [
        {
          hand_number: summary.hand_number,
          result,
          pot: summary.pot,
          board: summary.board,
          sb_hole: summary.hole[0] ?? null,
          bb_hole: summary.hole[1] ?? null,
          my_was_sb: myWasSb,
        },
        ...prev,
      ].slice(0, HISTORY_MAX),
    );
    // 触発条件は実質 summary.hand_number 変化のみ (= lastCapturedHandRef でガード)。
    // summary 参照自体は broadcast 毎に新規になるが、 setHistory が同値なら React は
    // skip するので毎 render 触発でも実害なし (= setTimeout 系のような cancel ループは無い)。
  }, [summary, mini, staticView, yourSide]);

  // hand_complete (= 通常 fold / showdown 経由) または all_in_run_out / showdown
  // (= 全 in 経由で resolve 前にここに stop しうる) に入って、 自分が次ハンド開始権
  // (= to_act) を持っているなら自動 advance。 短い delay で winner banner を視認可。
  //
  // onMove は BoardRenderer が `(mv) => onMove(mv)` で毎 render 新規 arrow を渡す
  // ので、 deps に直接入れると毎回 setTimeout が cleanup されて永久に発火しない
  // (= 「all-in に対して固まる」 の真因)。 ref 経由で primitive deps のみ列挙する。
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;
  const autoNextPhase =
    h.phase === 'hand_complete' || h.phase === 'all_in_run_out' || h.phase === 'showdown';
  useEffect(() => {
    if (mini || staticView) return;
    if (matchOver) return;
    if (!isMyTurn) return;
    if (!canNextHand) return;
    if (!autoNextPhase) return;
    const t = setTimeout(() => onMoveRef.current?.('next_hand'), NEXT_HAND_AUTO_DELAY_MS);
    return () => clearTimeout(t);
  }, [mini, staticView, matchOver, isMyTurn, canNextHand, autoNextPhase]);

  // mental poker protocol (= docs §8.10.X、 Golle 2005 on-demand pick、 PR B4-D):
  // 9 cards (= 4 hole + 5 board) を 1 枚ずつ commit-reveal + ElGamal pairwise channel
  // で配布。 useMentalPokerHand hook が wasm 経由で per-slot pick を駆動。
  //
  // ProvideMyHole / ProvideBoardCards は Play 階層の <MentalPokerDriver> が自動送信する。
  // handNum だけ store key として残し、 awaiting / boardLen は driver の責務に移管したので削除。
  const handNum = h.hand_number;
  // §8.10.A: protocol 駆動は Play 階層の <MentalPokerDriver> で常駐実行され、 結果 (myHole /
  // revealedBoard / cryptoReady / cheatDetected) は room store に書き込まれる。 ここでは
  // 描画用に読むだけ (= 駆動と描画の構造分離、 mini/full/active タブとは無関係に protocol が
  // 前進する)。 旧 `protocolActive = !mini && ...` が並列モードで両者デッドロックを起こして
  // いた問題への根本対処。
  const cryptoResults = useRoom((s) => {
    if (board == null) return null;
    return s.mps.get(`${board}:${handNum}`) ?? null;
  });
  const crypto = {
    myHole: cryptoResults?.myHole ?? null,
    revealedBoard: cryptoResults?.revealedBoard ?? EMPTY_BOARD,
    cryptoReady: cryptoResults?.cryptoReady ?? false,
    cheatDetected: cryptoResults?.cheatDetected ?? false,
    cryptoError: cryptoResults?.cryptoError ?? false,
  };
  const myHole = crypto.myHole ?? engineMyHole;

  // mental poker protocol stuck (= sk 喪失等で hole 復元不能) の検出:
  // board が公開済 / hand 終了済なのに自分の hole が None なら確実に失敗。
  // この時 PokerCard を 「??」 face で表示 + 「手札を復元できませんでした」 バナー。
  // docs §1.8/§8.10.B「face down betting」 経路では engine がこの状態で betting を許可する。
  const holeRecoveryFailed =
    mySeat != null &&
    (myHole == null || myHole[0] == null || myHole[1] == null) &&
    (h.board.length > 0 ||
      h.phase === 'hand_complete' ||
      h.phase === 'showdown' ||
      h.phase === 'all_in_run_out');

  // cheat 検出時の console.error (= e2e poker-*.spec で `cheat detected` 文字列を assert).
  // UI 側は下の banner + 既存「Fold (Failed Recover)」 ボタン (= awaiting_cards 中の fold) で
  // player が手動で hand を諦める path (§1.9 / §8.10.A 手動 fold) に乗る。 Play 階層では
  // MentalPokerDriver の active が false 化されて crypto 駆動が停止する (= docs §8.10.A (d))。
  useEffect(() => {
    if (crypto.cheatDetected) {
      console.error('[poker] mental poker cheat detected — opponent fake face down');
    }
  }, [crypto.cheatDetected]);
  // §8.10.A: ProvideMyHole / ProvideBoardCards の自動送信は <MentalPokerDriver> が Play 階層で
  // 行う (= mini / 非アクティブタブと無関係に動かす)。 旧実装はここで auto-provide していたが、
  // `if (mini || staticView) return` のせいで並列モードで player が他盤タブを見ている間 PK は
  // mini 描画されて provide が起きず、 「相手の手番のまま止まる」 永久 stall を起こしていた。

  // Mini 盤: ポーカーテーブルのシルエットだけ。 hooks は全て上で完了させてから分岐。
  if (mini) {
    return (
      <div className="poker-board mini" aria-label="Heads-Up Poker">
        <div className="poker-table-mini">
          <div className="poker-board-cards-mini">
            {h.board.slice(0, 5).map((c, i) => (
              <PokerCard key={`b-${i}`} card={c} />
            ))}
            {Array.from({ length: 5 - h.board.length }).map((_, i) => (
              <PokerCard key={`bb-${i}`} faceDown />
            ))}
          </div>
          <div className="poker-mini-pot">Pot: {h.pot}</div>
        </div>
      </div>
    );
  }

  const winnerLabel = (() => {
    if (h.winner == null) return null;
    // pot_split = [sb_get, bb_get]、 合計が hand 全 pot 額。 非 chop なら片方 = 全 pot、 もう片方 = 0。
    const totalPot = h.pot_split ? h.pot_split[0] + h.pot_split[1] : 0;
    if (h.winner.length === 2) {
      // chop: 自分が半分 (= pot_split[mySeat]) 取得
      const myShare = mySeat != null && h.pot_split ? h.pot_split[seatIdx(mySeat)] : 0;
      return `引き分け (chop) +${myShare}`;
    }
    const w = h.winner[0];
    if (mySeat != null && w === mySeat) return `勝ち +${totalPot}`;
    return `負け -${totalPot}`;
  })();

  return (
    <div
      className={`poker-board ${showOppHint ? 'not-my-turn' : ''}`}
      data-testid="poker-board"
    >
      {/* 左サイド: 直近ハンド履歴 (= 自動進行で消える結果を覚えておく) */}
      {history.length > 0 && (
        <div className="poker-history" data-testid="poker-history" aria-label="直近ハンド履歴">
          <div className="poker-history-title">直近ハンド</div>
          {history.map((hr) => {
            const myHole = hr.my_was_sb == null
              ? null
              : hr.my_was_sb ? hr.sb_hole : hr.bb_hole;
            const oppHole = hr.my_was_sb == null
              ? null
              : hr.my_was_sb ? hr.bb_hole : hr.sb_hole;
            const resultLabel = hr.result === 'win' ? '勝' : hr.result === 'lose' ? '負' : '分';
            return (
              <div
                key={hr.hand_number}
                className={`poker-history-entry ph-${hr.result}`}
              >
                <div className="ph-row">
                  <span className="ph-num">#{hr.hand_number}</span>
                  <span className={`ph-result ph-result-${hr.result}`}>{resultLabel}</span>
                  <span className="ph-pot">{hr.pot}</span>
                </div>
                {hr.board.length > 0 && (
                  <div className="ph-board-row" aria-label="board">
                    {hr.board.map((card, i) => <PokerInlineCard key={`bd-${i}`} card={card} />)}
                  </div>
                )}
                {(myHole != null || oppHole != null) && (
                  <div className="ph-cards">
                    {myHole && <HoleMini cards={myHole} label="自" />}
                    {oppHole && <HoleMini cards={oppHole} label="相" />}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 上端: 相手 */}
      <div className="poker-seat poker-seat-opp">
        <div className="poker-seat-info">
          <span className="poker-seat-label">
            相手 {oppSeat === 'sb' ? '(SB / dealer)' : '(BB)'}
          </span>
          <ChipStack amount={oppStack} />
          {oppRoundBet > 0 && <span className="poker-current-bet">Bet: {oppRoundBet}</span>}
        </div>
        <div className="poker-hole-cards">
          {[0, 1].map((i) => (
            <PokerCard
              key={i}
              card={oppHole?.[i] ?? null}
              faceDown={!showDown && !staticView}
            />
          ))}
        </div>
      </div>

      {/* community board (= 5 枚): .poker-center の中に置くと flex 1 1 0 + min-h:0 + overflow:auto
          で center が h=0 に潰れたとき clip されて見えなくなる。 必ず可視にしたいので center の
          外に出し、 親 .poker-board (flex column) の独立行として `flex-shrink: 0` で確保する。 */}
      <div className="poker-board-cards">
        {Array.from({ length: 5 }).map((_, i) => {
          // crypto.revealedBoard が engine h.board より一瞬先行しうる (= partial decrypt
          // 完了から provide_board_cards のラウンドトリップまでの brief gap)。 描画は両者の
          // OR で「見えてる方」 を出す ([[project_mixed_game_poker_mental_mvp]])。
          const c = h.board[i] ?? crypto.revealedBoard[i];
          if (!c) {
            return <PokerCard key={`slot-${i}`} faceDown />;
          }
          return <PokerCard key={`b-${i}`} card={c} />;
        })}
      </div>

      {/* 中央: ポット + ハンド情報 + winner banner (board cards は上に分離済) */}
      <div className="poker-center">
        <div className="poker-pot">
          Pot: <strong>{h.pot + h.current_round_bet[0] + h.current_round_bet[1]}</strong>
        </div>
        <div className="poker-hand-info">
          ハンド #{h.hand_number} / blinds {h.blinds[0]}/{h.blinds[1]}
          {h.bb_ante > 0 && ` / BB ante ${h.bb_ante}`}
        </div>
        {winnerLabel && (
          <div className="poker-winner-banner" data-testid="poker-winner">
            {winnerLabel}
          </div>
        )}
      </div>

      {/* 下端: 自分 */}
      <div className="poker-seat poker-seat-me">
        <div className="poker-hole-cards">
          {[0, 1].map((i) => (
            <PokerCard
              key={i}
              card={myHole?.[i] ?? null}
              failed={holeRecoveryFailed && myHole?.[i] == null}
            />
          ))}
        </div>
        {holeRecoveryFailed && (
          <div
            className="poker-hole-recovery-failed"
            data-testid="poker-hole-recovery-failed"
            role="status"
          >
            手札を復元できませんでした
          </div>
        )}
        {crypto.cheatDetected && (
          <div
            className="poker-cheat-detected"
            data-testid="poker-cheat-detected"
            role="alert"
          >
            相手の不正検出 — このハンドを Fold して破棄してください
          </div>
        )}
        {crypto.cryptoError && (
          <div
            className="poker-cheat-detected"
            data-testid="poker-crypto-error"
            role="alert"
          >
            暗号エンジンの読み込みに失敗しました — ページを再読み込みしてください
          </div>
        )}
        <div className="poker-seat-info">
          <span className="poker-seat-label">
            あなた {mySeat === 'sb' ? '(SB / dealer)' : '(BB)'}
          </span>
          <ChipStack amount={myStack} />
          {myRoundBet > 0 && <span className="poker-current-bet">Bet: {myRoundBet}</span>}
        </div>
      </div>

      {/* アクションバー: HandComplete (= next_hand のみ合法) は自動 advance するので非表示 */}
      {isMyTurn && !staticView && !canNextHand && (
        <div className="poker-action-bar">
              {can.has('fold') && (
                // awaiting 中 (= mental poker stuck) は Fold が「protocol 失敗で hand を諦める」
                // 意思表示 → ラベルを 「Fold (Failed Recover)」 にして通常 Fold と区別 (docs §1.9)。
                // 内部 move は同じ PokerMove::Fold で chip も通常 fold と同じ動き。
                <button
                  type="button"
                  className={`poker-action poker-action-fold${h.awaiting_cards != null ? ' poker-action-fold-failed-recover' : ''}`}
                  data-testid={h.awaiting_cards != null ? 'poker-fold-failed-recover' : 'poker-fold'}
                  onClick={() => onMove?.('fold')}
                  title={
                    h.awaiting_cards != null
                      ? 'mental poker protocol が前進不能 (= sk 喪失 / 通信断 等) な時に、 このハンドを諦めて pot を相手に渡す'
                      : undefined
                  }
                >
                  {h.awaiting_cards != null ? 'Fold (Failed Recover)' : 'Fold'}
                </button>
              )}
              {can.has('check') && (
                <button
                  type="button"
                  className="poker-action poker-action-check"
                  data-testid="poker-check"
                  onClick={() => onMove?.('check')}
                >
                  Check
                </button>
              )}
              {can.has('call') && (
                <button
                  type="button"
                  className="poker-action poker-action-call"
                  data-testid="poker-call"
                  onClick={() => onMove?.('call')}
                >
                  Call {toCall}
                </button>
              )}
              {/* Bet / Raise ボタンは Call の横 (= action 行内) に表示。 slider と preset は
                  下段の `.poker-bet-controls` に分離し、 額調整は下段、 確定は上段の流れ。 */}
              {canBetOrRaise && (
                <button
                  type="button"
                  className={`poker-action ${isRaiseMode ? 'poker-action-raise' : 'poker-action-bet'}`}
                  data-testid={isRaiseMode ? 'poker-raise' : 'poker-bet'}
                  onClick={commitBet}
                >
                  {isRaiseMode ? `Raise to ${betValue}` : `Bet ${betValue}`}
                </button>
              )}
              {!canBetOrRaise && can.has('all_in') && (
                // bet/raise 不可で all-in のみ合法 (= 短いスタックでコール額超) の場合
                <button
                  type="button"
                  className="poker-action poker-action-allin"
                  data-testid="poker-allin"
                  onClick={() => onMove?.('all_in')}
                >
                  All-In {allInTo}
                </button>
              )}
              {canBetOrRaise && (
                <div className="poker-bet-controls">
                  {/* 額調整 slider (= 上段 Bet/Raise ボタンの値と連動) */}
                  <input
                    type="range"
                    className="poker-bet-slider"
                    min={isRaiseMode ? minRaiseTo : minBet}
                    max={isRaiseMode ? maxRaiseTo : maxBet}
                    step={1}
                    value={betValue}
                    onChange={(e) => setBetValue(Number(e.target.value))}
                    data-testid="poker-bet-slider"
                    aria-label="Bet / Raise 額"
                  />
                  {/* size プリセット (= 2bb/2.5bb/3bb or 33/50/100% pot) + All-in */}
                  <div className="poker-bet-presets">
                    {(isPreflop
                      ? PREFLOP_OPENS.map((p) => ({ label: p.label, value: () => sizeByBb(p.mult) }))
                      : POT_FRACTIONS.map((p) => ({ label: p.label, value: () => sizeByPot(p.frac) }))
                    ).map((p) => (
                      <button
                        key={p.label}
                        type="button"
                        className="poker-size-btn"
                        data-testid={`poker-size-${p.label}`}
                        onClick={() => setBetValue(p.value())}
                      >
                        {p.label}
                      </button>
                    ))}
                    {can.has('all_in') && (
                      <button
                        type="button"
                        className="poker-size-btn poker-size-btn--allin"
                        data-testid="poker-size-allin"
                        onClick={() => setBetValue(allInTo)}
                      >
                        All-in
                      </button>
                    )}
                  </div>
                </div>
              )}
        </div>
      )}

      {showOppHint && (
        <div className="board-turn-hint" aria-hidden>
          手番待ち
        </div>
      )}
    </div>
  );
}

function seatIdx(s: Seat): 0 | 1 {
  return s === 'sb' ? 0 : 1;
}

// 旧 dummyCardUnique (= MVP fallback) は B4-D + provide useEffect cleanup で削除済。
// real mental poker (= Golle on-demand pick) の確定値を待ってから provide する設計に
// 移行した (= dummy で先送りすると engine が dummy を受理してしまい real value reject
// される問題があった、 hand reveal verify との不整合で cheat 誤検出も起きる)。

/** 履歴ペイン用の超小型ホールカード 2 枚表示。 サイド (自/相) + suit 色だけ識別できれば十分。 */
function HoleMini({ cards, label }: { cards: [Card, Card]; label: string }) {
  return (
    <span className="ph-hole">
      <span className="ph-hole-label">{label}</span>
      {cards.map((card, i) => <PokerInlineCard key={i} card={card} />)}
    </span>
  );
}
